import {
  Button,
  Chip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollShadow,
  Progress,
  Tooltip,
} from "@heroui/react";
import * as React from "react";
import { Icon } from "@iconify/react";
import { motion, AnimatePresence } from "framer-motion";
import { replaceWordInDocument, scrollToWord } from "../../taskpane"; // Add scrollToWord import
import { useTranslation } from "react-i18next";

interface SpellCheckProps {
  loading: boolean;
  corrections: { word: string; suggestions: string[] }[];
  addSkippingWord?: (word: string) => Promise<void>;
  refetchCorrections: () => Promise<void>;
}

export const SpellCheckPage: React.FC<SpellCheckProps> = ({
  corrections,
  loading,
  addSkippingWord,
  refetchCorrections,
}) => {
  const [replacing, setReplacing] = React.useState<boolean>(false);
  const [localCorrections, setLocalCorrections] = React.useState(corrections);
  const [rotate, setRotate] = React.useState(0);
  const { t } = useTranslation();

  React.useEffect(() => {
    setLocalCorrections(corrections);
  }, [corrections]);

  const handleReplace = async (original: string, replacement: string) => {
    setReplacing(true);
    await replaceWordInDocument(original, replacement);
    setLocalCorrections((prev) => prev.filter((c) => c.word !== original));
    setReplacing(false);
  };

  const handleAddSkippingWord = async (word: string) => {
    if (addSkippingWord) {
      await addSkippingWord(word);
      setLocalCorrections((prev) => prev.filter((c) => c.word !== word));
    }
  };

  const handleWordClick = async (word: string) => {
    try {
      await scrollToWord(word);
    } catch (err) {
      console.error(`Error scrolling to word "${word}":`, err);
    }
  };

  return (
    <div className="p-3 space-y-4 relative">
      <Button
        isDisabled={loading}
        size="sm"
        isIconOnly
        onPress={() => {
          setRotate((prev) => prev + 360);
          refetchCorrections();
        }}
        variant="light"
        className="absolute top-3 right-3"
      >
        <Tooltip content={t("refetchSpellcheck")} placement="bottom">
          <motion.div animate={{ rotate }} transition={{ duration: 0.6, ease: "easeInOut" }}>
            <Icon icon="bitcoin-icons:refresh-outline" width={30} height={30} />
          </motion.div>
        </Tooltip>
      </Button>
      {(loading || replacing) && (
        <div className="absolute top-0 left-0 w-full">
          <Progress color="success" isIndeterminate className="w-full rounded-full" size="sm" />
        </div>
      )}

      {localCorrections.length === 0 ? (
        <div className="text-center text-zinc-500 p-6">{t("noMistakeFound")}</div>
      ) : (
        <ScrollShadow
          hideScrollBar
          className="w-full p-2 rounded-xl flex flex-wrap content-start gap-2 overflow-y-auto max-h-[55vh] mt-4"
          orientation="vertical"
        >
          <AnimatePresence>
            {localCorrections.map((correction) => (
              <motion.div
                key={correction.word} // use unique key for proper animation
                initial={{ opacity: 0, scale: 0.9, y: 5 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -5 }}
                transition={{ duration: 0.2 }}
              >
                <Popover placement="bottom-start">
                  <PopoverTrigger>
                    <Chip
                      size="sm"
                      color="danger"
                      variant="flat"
                      className="cursor-pointer max-w-[120px] truncate transition-transform hover:scale-105"
                      onClick={() => handleWordClick(correction.word)}
                    >
                      {correction.word}
                    </Chip>
                  </PopoverTrigger>
                  <PopoverContent className="w-[160px] py-2 px-2 rounded-xl shadow-lg border border-zinc-200 bg-white">
                    <div className="flex flex-col gap-1">
                      {correction.suggestions.length == 0 ? (
                        <div className="text-center text-zinc-500">{t("noSuggestions")}</div>
                      ) : (
                        correction.suggestions.map((suggestion) => (
                          <Button
                            key={suggestion}
                            size="sm"
                            variant="light"
                            className="w-full justify-start text-xs text-zinc-700 hover:bg-zinc-100 rounded-md px-2 py-1 transition"
                            startContent={
                              <Icon icon="mdi:check" className="w-3 h-3 text-emerald-500" />
                            }
                            onPress={() => handleReplace(correction.word, suggestion)}
                          >
                            {suggestion}
                          </Button>
                        ))
                      )}
                      <div className="border-t border-zinc-200 my-1" />
                      <Button
                        size="sm"
                        variant="light"
                        className="w-full justify-start text-xs text-emerald-600 hover:bg-emerald-50 rounded-md px-2 py-1 transition"
                        startContent={<Icon icon="mdi:plus" className="w-3 h-3" />}
                        onPress={() => handleAddSkippingWord(correction.word)}
                      >
                        {t("addToVocabulary")}
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </motion.div>
            ))}
          </AnimatePresence>
        </ScrollShadow>
      )}
    </div>
  );
};